import React from 'react';
import {
  WebsiteDomainStatus,
  WebsiteDomainType,
  type WebsiteDomainRecord,
} from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { getWebsiteDomainHint } from '../../utils/website-domain.util';
import {
  WEBSITE_DOMAIN_STATUS_LABEL_KEYS,
  WEBSITE_DOMAIN_TYPE_LABEL_KEYS,
} from '../../utils/website-status-labels.util';
import { WebsiteDnsRecord } from './website-dns-record';

/** Props for one connected domain. */
interface WebsiteDomainCardProps {
  /** Domain with its DNS records and verification state. */
  domain: WebsiteDomainRecord;
  /** Whether the current user may not edit the website. */
  disabled: boolean;
  /** Whether a verification is being requested. */
  verifying: boolean;
  /** Checks the domain again right away. */
  onVerify: (domainId: number) => void;
}

/**
 * One domain: its state, what to do next and, for a custom domain, the DNS
 * records to create, in the order they appear during verification. The
 * provisional domain's records are managed for the customer, so they are
 * not shown.
 *
 * @param props - Domain, permissions and the verify action.
 * @returns The domain card.
 */
export const WebsiteDomainCard: React.FC<WebsiteDomainCardProps> = ({
  domain,
  disabled,
  verifying,
  onVerify,
}) => {
  const { t } = useI18n();
  const hint = getWebsiteDomainHint(domain);
  const isCustom = domain.type === WebsiteDomainType.CUSTOM;
  const canVerify = isCustom && domain.status !== WebsiteDomainStatus.ACTIVE;
  const isError = domain.status === WebsiteDomainStatus.ERROR;

  return (
    <div className="space-y-3 rounded-lg border border-neutral-200 p-3 sm:p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <Text weight="medium" className="break-all" disableAutoLink>
            {domain.hostname}
          </Text>
          <Text size="small" color="secondary">
            {isCustom
              ? t('websites.settings.domains.domainStatus', {
                  type: t(WEBSITE_DOMAIN_TYPE_LABEL_KEYS[domain.type]),
                  status: t(WEBSITE_DOMAIN_STATUS_LABEL_KEYS[domain.status]),
                })
              : t('websites.settings.domains.provisionalManaged')}
          </Text>
        </div>
        {canVerify ? (
          <Button
            className="shrink-0 self-start"
            size="sm"
            variant="secondary"
            appearance="outline"
            onClick={() => onVerify(domain.id)}
            disabled={disabled}
            loading={verifying}
          >
            {t('websites.settings.domains.actions.reviewStatus')}
          </Button>
        ) : null}
      </div>
      {hint ? (
        <div className="space-y-1">
          <Text size="small" color={isError ? 'danger' : 'inherit'}>
            {t(hint.key)}
          </Text>
          {hint.detail ? (
            <Text
              size="tiny"
              color="secondary"
              className="break-all"
              disableAutoLink
            >
              {t('websites.settings.domains.hints.detail', {
                detail: hint.detail,
              })}
            </Text>
          ) : null}
        </div>
      ) : null}
      {isCustom && domain.verificationRecordName && domain.verificationValue ? (
        <WebsiteDnsRecord
          title={t('websites.settings.domains.records.certificate')}
          type={domain.verificationRecordType ?? 'CNAME'}
          name={domain.verificationRecordName}
          value={domain.verificationValue}
        />
      ) : null}
      {isCustom && domain.cloudFrontDomainName ? (
        <WebsiteDnsRecord
          title={t('websites.settings.domains.records.site')}
          type="CNAME"
          name={domain.hostname}
          value={domain.cloudFrontDomainName}
        />
      ) : null}
    </div>
  );
};
